import { ANALYTICS_EVENTS, REPORT_TYPES } from '@packages/core/analytics';
import { Button, Label, Typography } from '@packages/ui/shared';
import { analyticsService } from '@web/services/singletons';
import { useCallback, useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { FailCodeModalContent, FailCodeSearchMethod } from '../fail-code-section.types';

const useFailCodeSearchMethodSelectorStyles = createUseStyles({
    buttonRow: {
        display: 'flex',
        gap: 15,
    },
});
/**
 * Props for the {@link FailCodeSearchMethodSelector} component.
 */
export type FailCodeSearchMethodSelectorProps = {
    /** Callback invoked with the selected search method when the user clicks "Continue". */
    onSearchMethodSelect: (method: FailCodeSearchMethod) => void;
    /** Callback invoked when the user clicks "Cancel" or closes the modal. */
    onClose: () => void;
    /** Callback to get the analytics option for a given search method. */
    getAnalyticsOption: (method: FailCodeSearchMethod | FailCodeModalContent) => string;
    /** Whether this selector is currently visible. Used to reset state when the modal opens. */
    show?: boolean;
};

/**
 * Search method selector for the fail code modal. Displays radio buttons for each available
 * fail code search method and a "Continue" button to proceed. Resets its selection state
 * whenever the modal becomes visible (so re-opening always starts clean).
 *
 * @param props - {@link FailCodeSearchMethodSelectorProps}
 * @returns the search method selector JSX
 */
export function FailCodeSearchMethodSelector({
    onSearchMethodSelect,
    show,
    onClose,
    getAnalyticsOption,
}: FailCodeSearchMethodSelectorProps) {
    // Hooks
    const { t } = useTranslation();
    const classes = useFailCodeSearchMethodSelectorStyles();

    // State
    const [selectedMethod, setSelectedMethod] = useState<FailCodeSearchMethod | null>(null);

    // Handlers

    /**
     * Handle a radio button selection by updating the selected search method.
     *
     * @param method - the search method that was selected
     */
    const handleMethodChange = useCallback((method: FailCodeSearchMethod) => {
        setSelectedMethod(method);
    }, []);

    /**
     * Handle the "Search by fail code" radio button selection.
     */
    const handleSearchByFailCodeChange = useCallback(() => {
        handleMethodChange('searchByFailCode');
    }, [handleMethodChange]);

    /**
     * Handle the "Search by part" radio button selection.
     */
    const handleSearchByPartChange = useCallback(() => {
        handleMethodChange('searchByPart');
    }, [handleMethodChange]);

    /**
     * Handle the "Lookup by section" radio button selection.
     */
    const handleLookupBySectionChange = useCallback(() => {
        handleMethodChange('lookupBySection');
    }, [handleMethodChange]);

    /**
     * Handle the "Lookup by electrical grid" radio button selection.
     */
    const handleLookupByElectricalGridChange = useCallback(() => {
        handleMethodChange('lookupByElectricalGrid');
    }, [handleMethodChange]);

    /**
     * Handle the "Continue" button press by passing the selected method to the parent.
     */
    const handleContinueButtonPress = useCallback(() => {
        if (selectedMethod) {
            onSearchMethodSelect(selectedMethod);
            analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_FAIL_CODE_SELECTED, {
                report_type: REPORT_TYPES.TECHLINE_CASE,
                option: getAnalyticsOption(selectedMethod),
            });
        }
    }, [getAnalyticsOption, onSearchMethodSelect, selectedMethod]);
    // Effects

    // Reset selection when the modal becomes visible
    useEffect(() => {
        if (show) {
            setSelectedMethod(null);
        }
    }, [show]);

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>{t('qmr:sections:failCode.failCodeSearch')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Group>
                    <div className="mb-6">
                        <Label>{t('qmr:sections:failCode.selectOptions')}.</Label>
                    </div>

                    <Form.Check
                        checked={selectedMethod === 'searchByFailCode'}
                        className="mb-3"
                        id="searchFailCode"
                        label={t('qmr:sections:failCode.searchFailCode')}
                        name="failCodeSearch"
                        onChange={handleSearchByFailCodeChange}
                        type="radio"
                    />

                    <Form.Check
                        checked={selectedMethod === 'searchByPart'}
                        className="mb-3"
                        id="searchFailCodeByPart"
                        label={t('qmr:sections:failCode.searchFailCodeByPart')}
                        name="failCodeSearch"
                        onChange={handleSearchByPartChange}
                        type="radio"
                    />

                    <Form.Check
                        checked={selectedMethod === 'lookupBySection'}
                        className="mb-3"
                        id="lookupBySection"
                        label={t('qmr:sections:failCode.lookupBySection')}
                        name="failCodeSearch"
                        onChange={handleLookupBySectionChange}
                        type="radio"
                    />

                    <Form.Check
                        checked={selectedMethod === 'lookupByElectricalGrid'}
                        className="mb-3"
                        id="lookupByElectricalGridSection"
                        label="Lookup by electrical grid location"
                        name="failCodeSearch"
                        onChange={handleLookupByElectricalGridChange}
                        type="radio"
                    />

                    {/* <Form.Check
                        checked={selectedMethod === 'noTroubleFound'}
                        className="mb-3"
                        id="noTroubleFound"
                        label={t('qmr:sections:failCode.noTroubleFound')}
                        name="failCodeSearch"
                        onChange={handleNoTroubleFoundChange}
                        type="radio"
                    /> */}
                </Form.Group>
            </Modal.Body>

            <Modal.Footer>
                <div className={classes.buttonRow}>
                    <Button onPress={onClose} variant="link-blue">
                        <Typography color="blueOne">{t('qmr:sections:failCode.cancel')}</Typography>
                    </Button>
                    <Button disabled={!selectedMethod} onPress={handleContinueButtonPress} variant="primary">
                        {t('qmr:sections:failCode.continue')}
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}
